import { useState } from 'react';
import { useClientTime, sourceLabel } from '../../api/time.ts';
import { byDay, CLIENT_PRESETS, presetRange, type ClientPreset } from '../../time/view.ts';
import { describeMinutes } from '../../duration.ts';
import { formatDate } from '../../dates.ts';
import { agencyToday } from '../../components/tasks/FollowUp.tsx';
import { chip, plain } from '../../components/tasks/Fields.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';
import { SectionPanel } from '../ClientRecord.tsx';
import { monthLabel } from '../../time/days.ts';

/**
 * A client's Time section (money plan phase 1, 161): tiers 1 and 2 only, "for now" (Arthur, A-#329); the server's
 * sections rule draws it and the route refuses anyone else. Who spent how long on the client: every kind of time
 * (task completions and actions, welfare visits, site visits, communications), by day, with totals by month and by
 * department over the WHOLE range, so they read the same on every page. Exact minutes, never rounded.
 */

export function TimeSection({ clientId }: { clientId: string }) {
  const [preset, setPreset] = useState<ClientPreset>('year');
  const today = agencyToday();
  const q = useClientTime(clientId, presetRange(preset, today));
  const pages = q.data?.pages ?? [];
  const first = pages[0];
  const entries = pages.flatMap((p) => p.entries);

  return (
    <SectionPanel title="Time">
      <div role="tablist" aria-label="Period" className="flex flex-wrap gap-2">
        {CLIENT_PRESETS.map((p) => (
          <button key={p.key} type="button" role="tab" aria-selected={preset === p.key} className={chip(preset === p.key)}
                  onClick={() => setPreset(p.key)}>{p.label}</button>
        ))}
      </div>
      {q.error && !q.data ? <LoadFailure what="This client's time" error={q.error} onRetry={() => void q.refetch()} />
        : !first ? <p className="py-3 text-neutral-700">Loading…</p>
        : first.totals.minutes === 0 ? (
          <p className="py-3 text-neutral-700">
            No time recorded for this client between {formatDate(first.from)} and {formatDate(first.to)}.
          </p>
        ) : (
          <>
            <p className="mt-3 text-neutral-900">
              <span className="font-semibold">{describeMinutes(first.totals.minutes)}</span> recorded between{' '}
              {formatDate(first.from)} and {formatDate(first.to)}: tasks and actions, welfare visits, site visits and
              communications.
            </p>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <section aria-labelledby="time-month">
                <h3 id="time-month" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">By month</h3>
                <table className="mt-1 w-full text-sm">
                  <tbody>
                    {first.totals.by_month.map((m) => (
                      <tr key={m.month} className="border-b border-neutral-200">
                        <td className="py-1.5 text-neutral-900">{monthLabel(m.month)}</td>
                        <td className="py-1.5 text-right tabular-nums text-neutral-900">{describeMinutes(m.minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <section aria-labelledby="time-dept">
                <h3 id="time-dept" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">By department</h3>
                <table className="mt-1 w-full text-sm">
                  <tbody>
                    {first.totals.by_department.map((d) => (
                      <tr key={d.department} className="border-b border-neutral-200">
                        <td className="py-1.5 text-neutral-900">{d.department}</td>
                        <td className="py-1.5 text-right tabular-nums text-neutral-900">{describeMinutes(d.minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </div>
            <section aria-labelledby="time-days" className="mt-4">
              <h3 id="time-days" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">By day</h3>
              {byDay(entries).map((d) => (
                <div key={d.day} className="mt-2">
                  <h4 className="flex justify-between border-b border-neutral-300 py-1 text-sm font-semibold text-neutral-900">
                    <span>{formatDate(d.day)}</span><span className="tabular-nums">{describeMinutes(d.minutes)}</span>
                  </h4>
                  <ul>
                    {d.entries.map((e, i) => (
                      <li key={`${d.day}-${i}`} className="flex flex-wrap justify-between gap-2 py-1.5 text-sm text-neutral-800">
                        <span>{e.person ?? 'Someone no longer on staff'} · {e.department} · {sourceLabel(e.source, e.is_action)}</span>
                        <span className="tabular-nums">{describeMinutes(e.minutes)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {q.hasNextPage && (
                <button type="button" className={`${plain} mt-3`} disabled={q.isFetchingNextPage}
                        onClick={() => void q.fetchNextPage()}>
                  {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              )}
            </section>
          </>
        )}
      <p className="mt-3 text-sm text-neutral-700">Shown to tiers 1 and 2. Minutes are exact: nothing here is rounded.</p>
    </SectionPanel>
  );
}
